import { Link } from "react-router-dom";
import type { OnboardingItem } from "../lib/contracts";
import { safeReasonForItem, titleForItem } from "../lib/reasons";
import { linkForRole, type OperationalLink } from "../lib/linkPermissions";
import type { EffectiveRole } from "../../../lib/roles";

export interface ChecklistItemCardProps {
  item: OnboardingItem;
  role: EffectiveRole;
  actionSlot?: React.ReactNode;
}

export function ChecklistItemCard({
  item, role, actionSlot,
}: ChecklistItemCardProps) {
  const title = titleForItem(item.key);
  const reason = safeReasonForItem(item);
  const link: OperationalLink | null = linkForRole(item, role);
  const reasonId = reason ? `${item.key}-reason` : undefined;

  const classificationLabel =
    item.classification === "acknowledged"
      ? "Admin confirmation"
      : item.classification === "automatic"
      ? "Automatic"
      : "Setup item";

  const statusText =
    item.status === "complete"
      ? "Complete"
      : item.status === "incomplete"
      ? "Incomplete"
      : "Unknown";

  const statusClass =
    item.status === "complete"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300"
      : item.status === "incomplete"
      ? "text-amber-800 bg-amber-50 border-amber-200 dark:bg-amber-950 dark:text-amber-300"
      : "text-slate-700 bg-slate-100 border-slate-200 dark:bg-slate-800 dark:text-slate-200";

  return (
    <article
      className="rounded-md border p-4 dark:border-slate-700"
      aria-describedby={reasonId}
      data-testid={`checklist-item-${item.key}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold" data-testid={`checklist-item-${item.key}-title`}>
            {title}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">{classificationLabel}</p>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass}`}
          data-testid={`checklist-item-${item.key}-status`}
        >
          <span aria-hidden="true">
            {item.status === "complete" ? "✓" : item.status === "incomplete" ? "!" : "?"}
          </span>
          {statusText}
        </span>
      </header>
      {reason && (
        <p
          id={reasonId}
          className="mt-2 text-sm text-slate-700 dark:text-slate-300"
          data-testid={`checklist-item-${item.key}-reason`}
        >
          {reason}
        </p>
      )}
      {item.acknowledged_at && item.status === "complete" && (
        <p className="mt-2 text-xs text-slate-500" data-testid={`checklist-item-${item.key}-ack-meta`}>
          Confirmed at {item.acknowledged_at}
          {item.acknowledged_by ? ` by ${item.acknowledged_by}` : ""}
        </p>
      )}
      {(link || actionSlot) && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {link && (
            <Link
              to={link.href}
              className="text-sm font-medium text-blue-700 underline dark:text-blue-400"
              data-testid={`checklist-item-${item.key}-link`}
              data-destination={link.destination}
            >
              {link.label}
            </Link>
          )}
          {actionSlot}
        </div>
      )}
    </article>
  );
}
